import { Suspense, type ReactElement } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { Layout } from '@/components/Layout';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { RouteLoadingFallback } from '@/components/RouteLoadingFallback';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RequireProperty } from '@/auth/RequireProperty';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { AppUpdateBoundary } from '@/offline/AppUpdateBoundary';

const ComingSoonPage = lazyWithRetry(() => import('@/pages/ComingSoonPage').then(({ ComingSoonPage }) => ({ default: ComingSoonPage })));
const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage').then(({ ProfilePage }) => ({ default: ProfilePage })));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/ForgotPasswordPage').then(({ ForgotPasswordPage }) => ({ default: ForgotPasswordPage })));
const HomePage = lazyWithRetry(() => import('@/pages/HomePage').then(({ HomePage }) => ({ default: HomePage })));
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage').then(({ LoginPage }) => ({ default: LoginPage })));
const RegisterPage = lazyWithRetry(() => import('@/pages/RegisterPage').then(({ RegisterPage }) => ({ default: RegisterPage })));
const ResendPasswordResetPage = lazyWithRetry(() => import('@/pages/ResendPasswordResetPage').then(({ ResendPasswordResetPage }) => ({ default: ResendPasswordResetPage })));
const ResendVerificationPage = lazyWithRetry(() => import('@/pages/ResendVerificationPage').then(({ ResendVerificationPage }) => ({ default: ResendVerificationPage })));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage').then(({ ResetPasswordPage }) => ({ default: ResetPasswordPage })));
const VerifyEmailPage = lazyWithRetry(() => import('@/pages/VerifyEmailPage').then(({ VerifyEmailPage }) => ({ default: VerifyEmailPage })));
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage').then(({ DashboardPage }) => ({ default: DashboardPage })));
const AuditLogPage = lazyWithRetry(() => import('@/pages/audit/AuditLogPage').then(({ AuditLogPage }) => ({ default: AuditLogPage })));
const BookingDetailPage = lazyWithRetry(() => import('@/pages/bookings/BookingDetailPage').then(({ BookingDetailPage }) => ({ default: BookingDetailPage })));
const BookingFormPage = lazyWithRetry(() => import('@/pages/bookings/BookingFormPage').then(({ BookingFormPage }) => ({ default: BookingFormPage })));
const BookingListPage = lazyWithRetry(() => import('@/pages/bookings/BookingListPage').then(({ BookingListPage }) => ({ default: BookingListPage })));
const CleanupPage = lazyWithRetry(() => import('@/pages/cleanup/CleanupPage').then(({ CleanupPage }) => ({ default: CleanupPage })));
const GuestDetailPage = lazyWithRetry(() => import('@/pages/guests/GuestDetailPage').then(({ GuestDetailPage }) => ({ default: GuestDetailPage })));
const GuestFormPage = lazyWithRetry(() => import('@/pages/guests/GuestFormPage').then(({ GuestFormPage }) => ({ default: GuestFormPage })));
const GuestListPage = lazyWithRetry(() => import('@/pages/guests/GuestListPage').then(({ GuestListPage }) => ({ default: GuestListPage })));
const PrivacyPolicyPage = lazyWithRetry(() => import('@/pages/legal/LegalPages').then(({ PrivacyPolicyPage }) => ({ default: PrivacyPolicyPage })));
const TermsAndConditionPage = lazyWithRetry(() => import('@/pages/legal/LegalPages').then(({ TermsAndConditionPage }) => ({ default: TermsAndConditionPage })));
const PlatformPropertiesPage = lazyWithRetry(() => import('@/pages/platform/PlatformPropertiesPage').then(({ PlatformPropertiesPage }) => ({ default: PlatformPropertiesPage })));
const PlatformPropertyDetailPage = lazyWithRetry(() => import('@/pages/platform/PlatformPropertyDetailPage').then(({ PlatformPropertyDetailPage }) => ({ default: PlatformPropertyDetailPage })));
const BookingLinkPage = lazyWithRetry(() => import('@/pages/public/BookingLinkPage').then(({ BookingLinkPage }) => ({ default: BookingLinkPage })));
const InvitationAcceptPage = lazyWithRetry(() => import('@/pages/public/InvitationAcceptPage').then(({ InvitationAcceptPage }) => ({ default: InvitationAcceptPage })));
const ReportsPage = lazyWithRetry(() => import('@/pages/reports/ReportsPage').then(({ ReportsPage }) => ({ default: ReportsPage })));
const BookingChannelsPage = lazyWithRetry(() => import('@/pages/settings/BookingChannelsPage').then(({ BookingChannelsPage }) => ({ default: BookingChannelsPage })));
const PropertyMembersPage = lazyWithRetry(() => import('@/pages/settings/PropertyMembersPage').then(({ PropertyMembersPage }) => ({ default: PropertyMembersPage })));
const PropertyProfilePage = lazyWithRetry(() => import('@/pages/settings/PropertyProfilePage').then(({ PropertyProfilePage }) => ({ default: PropertyProfilePage })));
const RoomsPage = lazyWithRetry(() => import('@/pages/settings/RoomsPage').then(({ RoomsPage }) => ({ default: RoomsPage })));
const RoomDetailPage = lazyWithRetry(() => import('@/pages/settings/RoomDetailPage').then(({ RoomDetailPage }) => ({ default: RoomDetailPage })));
const OnboardingPage = lazyWithRetry(() => import('@/pages/onboarding/OnboardingPage').then(({ OnboardingPage }) => ({ default: OnboardingPage })));
const PropertyCreatePage = lazyWithRetry(() => import('@/pages/onboarding/PropertyCreatePage').then(({ PropertyCreatePage }) => ({ default: PropertyCreatePage })));
const VerifyReceiptPage = lazyWithRetry(() => import('@/pages/receipts/VerifyReceiptPage').then(({ VerifyReceiptPage }) => ({ default: VerifyReceiptPage })));
const NotificationsPage = lazyWithRetry(() => import('@/pages/notifications/NotificationsPage').then(({ NotificationsPage }) => ({ default: NotificationsPage })));

function routeElement(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    // Router-level error boundary. Any error thrown while matching or
    // rendering a route — including the 404 the router raises for an
    // unmatched URL (the `*` route at the bottom) — is caught here and
    // rendered as a branded page, never React Router's default developer
    // error screen.
    errorElement: <RouteErrorBoundary />,
    element: <AppUpdateBoundary />,
    children: [
      {
        path: '/',
        element: routeElement(<HomePage />),
      },
      {
        path: '/coming-soon',
        element: routeElement(<ComingSoonPage />),
      },
      {
        path: '/register',
        element: routeElement(<RegisterPage />),
      },
      {
        path: '/login',
        element: routeElement(<LoginPage />),
      },
      {
        path: '/forgot-password',
        element: routeElement(<ForgotPasswordPage />),
      },
      {
        path: '/forgot-password/resend',
        element: routeElement(<ResendPasswordResetPage />),
      },
      {
        path: '/reset-password',
        element: routeElement(<ResetPasswordPage />),
      },
      {
        path: '/verify-email',
        element: routeElement(<VerifyEmailPage />),
      },
      {
        path: '/verify-email/resend',
        element: routeElement(<ResendVerificationPage />),
      },
      {
        path: '/privacy-policy',
        element: routeElement(<PrivacyPolicyPage />),
      },
      {
        path: '/terms-and-condition',
        element: routeElement(<TermsAndConditionPage />),
      },
      // Guest-facing, unauthenticated: reached only through possession of an
      // unguessable per-booking token, never through the app shell/nav (see
      // App\Http\Controllers\Api\V1\Public\BookingLinkController).
      {
        path: '/booking-links/:token',
        element: routeElement(<BookingLinkPage />),
      },
      // Staff-invitation acceptance — also public/unauthenticated (see
      // App\Http\Controllers\Api\V1\PropertyMembershipController::
      // showInvitation()/acceptInvitation()), same standalone-layout pattern
      // as the booking-link page above rather than the authenticated shell.
      {
        path: '/invitations/:token',
        element: routeElement(<InvitationAcceptPage />),
      },
      {
        element: <ProtectedRoute />,
        children: [
          // Onboarding routes deliberately sit outside Layout (no nav/app
          // shell yet — a user reaching these has no property, so most of
          // the shell would have nothing to point at).
          { path: '/onboarding', element: routeElement(<OnboardingPage />) },
          { path: '/properties/new', element: routeElement(<PropertyCreatePage />) },
          {
            element: <Layout />,
            children: [
              // Account-level, available before any property exists: the
              // dashboard shows its own "get started" state, and the profile
              // page never needs a property.
              { path: '/app', element: routeElement(<DashboardPage />) },
              { path: '/profile', element: routeElement(<ProfilePage />) },
              // Account-level: a user's own notifications may span properties,
              // so this sits outside RequireProperty (like the profile page).
              { path: '/notifications', element: routeElement(<NotificationsPage />) },
              // Property-scoped: RequireProperty renders a "create your
              // property" empty state (rather than letting property-scoped
              // API calls fail) when the user has no property yet.
              {
                element: <RequireProperty />,
                children: [
                  { path: '/guests', element: routeElement(<GuestListPage />) },
                  { path: '/guests/new', element: routeElement(<GuestFormPage />) },
                  { path: '/guests/:id', element: routeElement(<GuestDetailPage />) },
                  { path: '/bookings', element: routeElement(<BookingListPage />) },
                  { path: '/bookings/new', element: routeElement(<BookingFormPage />) },
                  { path: '/bookings/:id', element: routeElement(<BookingDetailPage />) },
                  { path: '/settings/rooms', element: routeElement(<RoomsPage />) },
                  { path: '/settings/rooms/:id', element: routeElement(<RoomDetailPage />) },
                  { path: '/settings/booking-channels', element: routeElement(<BookingChannelsPage />) },
                  { path: '/settings/property', element: routeElement(<PropertyProfilePage />) },
                  { path: '/settings/members', element: routeElement(<PropertyMembersPage />) },
                  { path: '/reports', element: routeElement(<ReportsPage />) },
                  { path: '/verify-receipt', element: routeElement(<VerifyReceiptPage />) },
                  { path: '/audit-log', element: routeElement(<AuditLogPage />) },
                  { path: '/cleanup', element: routeElement(<CleanupPage />) },
                ],
              },
              // Cross-tenant, no PropertyContext — gated by `is_platform_admin`
              // server-side (App\Http\Middleware\EnsurePlatformAdmin), not a
              // spatie permission, so these live behind the same ProtectedRoute
              // auth check as every tenant-scoped route rather than a
              // RequirePermission check. Not property-scoped, so outside
              // RequireProperty.
              { path: '/platform/properties', element: routeElement(<PlatformPropertiesPage />) },
              { path: '/platform/properties/:id', element: routeElement(<PlatformPropertyDetailPage />) },
            ],
          },
        ],
      },
      // Catch-all: any URL that matches nothing above renders the branded
      // 404 page instead of bubbling a router error.
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
